import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/router'
import { HTMLAttributes, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { toast } from '@/hooks/use-toast'

import { EdunodesExistingAccountNotice } from '@/components/auth/edunodes-existing-account-notice'
import { Button } from '@/components/custom/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'

import type { EdunodesCommunitySummary } from '@/constant/edunodes'
import authService from '@/services/auth.service'
import { ErrorResponse } from '@/utils/axios-error-handle'

type ForgotPasswordFormProps = HTMLAttributes<HTMLDivElement> & {
  buttonClassName?: string
  defaultEmail?: string
  edunodesCommunities?: EdunodesCommunitySummary[]
}

const formSchema = z.object({
  email: z.string().min(1, { message: 'Please enter your email' }).email({ message: 'Invalid email address' }),
})

export function ForgotPasswordForm({
  className,
  buttonClassName,
  defaultEmail = '',
  edunodesCommunities,
  ...props
}: ForgotPasswordFormProps) {
  const [isLoading, setIsLoading] = useState(false)

  const router = useRouter()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: defaultEmail },
  })

  useEffect(() => {
    if (defaultEmail) {
      form.setValue('email', defaultEmail)
    }
  }, [defaultEmail, form])

  async function onSubmit(data: z.infer<typeof formSchema>) {
    setIsLoading(true)
    try {
      const responseData = await authService.forgotPassword({
        email: data.email,
      })
      if (responseData.success) {
        router.push('/auth/reset-password/success')
      }
    } catch (error) {
      const _error = error as ErrorResponse
      toast({
        variant: 'destructive',
        title: 'Error sending email',
        description: _error.response.data.message || 'Error sending email',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={cn('grid gap-6', className)} {...props}>
      {edunodesCommunities && edunodesCommunities.length > 0 ? (
        <EdunodesExistingAccountNotice communities={edunodesCommunities} />
      ) : null}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="name@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button className={cn('mt-2', buttonClassName)} loading={isLoading}>
              Send
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
